import React, { useEffect, useMemo, useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import adminApi from "../api/adminApi";
import ConfirmDialog from "./ConfirmDialog";

export default function ProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [type, setType] = useState("all");
  const [deleting, setDeleting] = useState(null);

  async function load() {
    setLoading(true);
    setError("");
    try {
      setProjects((await adminApi.getProjects()) || []);
    } catch (err) {
      setError(adminApi.getErrorMessage(err, "Could not load projects"));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  const types = useMemo(() => [...new Set(projects.map((p) => p.type).filter(Boolean))].sort(), [projects]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return [...projects]
      .filter((p) => type === "all" || p.type === type)
      .filter((p) => !needle || [p.projectId, p.type, p.location, p.owner, p.capacity].some((v) => String(v || "").toLowerCase().includes(needle)))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [projects, query, type]);

  async function confirmDelete() {
    const project = deleting;
    setDeleting(null);
    try {
      // Clean up hosted images before removing the project itself.
      for (const imageUrl of project.images || []) {
        await adminApi.deleteProjectImage(project._id, imageUrl);
      }
      await adminApi.deleteProject(project._id);
      setProjects((items) => items.filter((item) => item._id !== project._id));
    } catch (err) {
      setError(adminApi.getErrorMessage(err, "Project could not be deleted"));
      load();
    }
  }

  return (
    <div className="admin-page-stack">
      {error && <div className="admin-alert error"><span>{error}</span><button onClick={load}><ReloadOutlined /> Retry</button></div>}
      <section className="admin-panel">
        <div className="admin-toolbar">
          <div className="admin-search"><SearchOutlined /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by ID, owner, location…" /></div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="admin-select"><option value="all">All categories</option>{types.map((item) => <option key={item}>{item}</option>)}</select>
          <Link to="/admin/projects/new" className="admin-primary-button"><PlusOutlined /> Add project</Link>
        </div>
        {!loading && <div className="admin-result-count">{filtered.length} of {projects.length} projects</div>}
        {loading ? <ProjectSkeleton /> : filtered.length ? (
          <div className="admin-project-grid">
            {filtered.map((project) => (
              <article className="admin-project-card" key={project._id}>
                <div className="admin-card-image">
                  {project.images?.[0] ? <img src={project.images[0]} alt={project.projectId || project.type} /> : <div className="admin-image-placeholder"><FolderOpenOutlined /><span>No image</span></div>}
                  <span className="admin-category-badge">{project.type}</span>
                  {project.images?.length > 1 && <span className="admin-image-count">+{project.images.length - 1}</span>}
                </div>
                <div className="admin-card-body">
                  <div><span className="admin-project-id">{project.projectId || "ID not assigned"}</span><h3>{project.owner || "Unnamed client"}</h3></div>
                  <div className="admin-card-facts"><span><small>Location</small>{project.location || "—"}</span><span><small>Capacity</small>{project.capacity || "—"}</span></div>
                  <div className="admin-card-actions">
                    <Link to={`/admin/projects/${project._id}/edit`}><EditOutlined /> Edit</Link>
                    <button onClick={() => setDeleting(project)}><DeleteOutlined /> Delete</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="admin-empty"><SearchOutlined /><h3>{projects.length ? "No matching projects" : "No projects yet"}</h3><p>{projects.length ? "Try a different search or category filter." : "Add your first solar installation to the portfolio."}</p>{!projects.length && <Link to="/admin/projects/new" className="admin-primary-button">Create project</Link>}</div>
        )}
      </section>
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete this project?"
        description={`This permanently deletes ${deleting?.projectId || "the project"} and attempts to remove all of its hosted images.`}
        confirmLabel="Delete project"
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function ProjectSkeleton() {
  return <div className="admin-project-grid">{[1,2,3,4,5,6].map((n) => <div key={n} className="admin-skeleton project-card" />)}</div>;
}
